"use client"
import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { FAQ, FAQCategory } from '@/lib/api/faq';

interface PublicExpandableCardProps {
  category: FAQCategory;
}

interface ExpandableItemProps {
  faq: FAQ;
  isExpanded: boolean;
  onToggle: () => void;
}

const ExpandableItem: React.FC<ExpandableItemProps> = ({ faq, isExpanded, onToggle }) => {
  return (
    <div className="mb-2">
      <button
        onClick={onToggle}
        className="w-full px-4 py-4 text-left flex items-center justify-between bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
        aria-expanded={isExpanded}
      >
        <span className="font-medium text-gray-900  max-sm:text-sm">{faq.question}</span>
        {isExpanded ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
        )}
      </button>
      {isExpanded && (
        <div className="bg-white mt-3 border border-gray-200  rounded-lg px-4 py-4">
          <div 
            className="text-gray-600 leading-relaxed max-sm:text-sm faq-content"
            dangerouslySetInnerHTML={{ __html: faq.answer }}
          />
        </div>
      )}
    </div>
  );
};

const PublicExpandableCard: React.FC<PublicExpandableCardProps> = ({ category }) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleItem = (faqId: string) => {
    const newExpandedItems = new Set(expandedItems);
    if (newExpandedItems.has(faqId)) {
      newExpandedItems.delete(faqId);
    } else {
      newExpandedItems.add(faqId);
    }
    setExpandedItems(newExpandedItems);
  };

  // Sort FAQs by position
  const sortedFaqs = category.faqs ? [...category.faqs].sort((a, b) => a.position - b.position) : [];

  return (
    <div className="mb-8">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">{category.name}</h3>
      <div>
        {sortedFaqs.length > 0 ? (
          sortedFaqs.map((faq) => (
            <ExpandableItem
              key={faq.id}
              faq={faq}
              isExpanded={expandedItems.has(faq.id)}
              onToggle={() => toggleItem(faq.id)}
            />
          ))
        ) : (
          <div className="text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
            No FAQs available in this category
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicExpandableCard;
